const automaticReports = require('../automaticReports')
const traccarHelper = require('../util/traccar')
const {
  getDates, getTranslations, convertMS, isClientSide, convertToLocaleString, convertToLocaleDateString,
  convertToLocaleTimeString
} = require('../util/utils')
const { getUserPartner } = require('fleetmap-partners')
const { getDataByDay } = require('../util/trips')
const jsPDF = require('jspdf')
const { headerFromUser, addTable } = require('../util/pdfDocument')
const { getStyle } = require('../reportStyle')

async function createDelayedStartReport (from, to, userData, traccar) {
  const reportData = {
    devices: [],
    from,
    to
  }

  const devices = userData.devices

  const sliced = automaticReports.sliceArray(devices, 5)
  let deviceCount = 0
  for (const slice of sliced) {
    const allInOne = await traccarHelper.getAllInOne(traccar, from, to, slice, true, true, false, false, deviceCount, devices.length)

    for (const d of slice) {
      const deviceData = await processDeviceData(from, to, allInOne, d, userData, traccar)
      reportData.devices.push(deviceData)
    }

    deviceCount = deviceCount + slice.length
  }

  reportData.devices = reportData.devices.flat()

  return reportData
}

function getDelayDescription (startTime, activityStart, lowDelay, mediumDelay, highDelay) {
  const startTimeDate = new Date(startTime)
  const activityStartDate = new Date(startTime)
  activityStartDate.setHours(activityStart.substring(0, 2), activityStart.substring(3, 5))

  if (startTimeDate >= activityStartDate) {
    const delay = startTimeDate.getTime() - activityStartDate.getTime()
    if (delay < (lowDelay * 60 * 1000)) {
      return '-'
    } else if (delay < (mediumDelay * 60 * 1000)) {
      return convertMS(delay, false) + ' - Low (+' + lowDelay + ')'
    } else if (delay < (highDelay * 60 * 1000)) {
      return convertMS(delay, false) + ' - Medium (+' + mediumDelay + ')'
    } else {
      return convertMS(delay, false) + ' - High (+' + highDelay + ')'
    }
  }

  return ''
}

async function processDeviceData (from, to, allInOne, d, userData, traccar) {
  const trips = allInOne.trips.filter(t => t.deviceId === d.id)
  const route = allInOne.route.filter(t => t.deviceId === d.id)

  const translations = getTranslations(userData)

  const weekDays = [
    translations.report.sunday,
    translations.report.monday,
    translations.report.tuesday,
    translations.report.wednesday,
    translations.report.thursday,
    translations.report.friday,
    translations.report.saturday
  ]

  const dates = getDates(from, to, userData.user.attributes.timezone || getUserPartner(userData.user).timezone)

  const days = []
  for (const date of dates) {
    const { tripsByDay, routeByDay } = await getDataByDay(d, date, { trips, route }, userData, traccar)

    let startTime
    let endTime
    if (userData.useIgnition) {
      const firstIgnitionOn = routeByDay.find(p => p.attributes.ignition)
      if (firstIgnitionOn) {
        startTime = firstIgnitionOn.fixTime
      }
    } else {
      if (tripsByDay.length) {
        startTime = tripsByDay[0].startTime
      }
    }

    if (tripsByDay.length) {
      endTime = tripsByDay[tripsByDay.length - 1].endTime
    }

    if (startTime) {
      const deviceDayData = {
        device: d.name,
        groupId: d.groupId,
        licensePlate: d.attributes.license_plate,
        driver: '',
        weekDay: weekDays[new Date(date).getDay()],
        startTime,
        delayDescription: getDelayDescription(startTime, userData.activityStart, userData.lowDelay, userData.mediumDelay, userData.highDelay),
        endTime
      }

      days.push(deviceDayData)
    }
  }

  return days
}

async function exportDelayedStartReportToPDF (userData, reportData) {
  console.log('exportDelayedStartReportToPDF')

  const timezone = userData.user.attributes.timezone
  const translations = getTranslations(userData)
  const lang = userData.user.attributes.lang || (isClientSide() && navigator.language)
  const reportDataRows = reportData.devices

  const headers = [
    translations.report.group,
    translations.report.vehicle,
    translations.report.driver,
    translations.report.day,
    translations.report.startDate,
    translations.report.startTime,
    translations.report.delayDescription,
    translations.report.endTime
  ]

  if (reportDataRows) {
    // eslint-disable-next-line new-cap
    const doc = new jsPDF.jsPDF('l')
    await headerFromUser(doc, translations.report.titleDelayedStartReport, userData.user)

    doc.setFontSize(13)
    doc.text(convertToLocaleString(reportData.from, lang, timezone, userData.user) + ' - ' + convertToLocaleString(reportData.to, lang, timezone, userData.user), 20, 25)

    const data = []
    reportDataRows.forEach(function (row) {
      const group = userData.groups.find(g => row.groupId === g.id)

      const temp = [
        group ? group.name : '',
        row.device,
        row.driver,
        row.weekDay,
        convertToLocaleDateString(row.startTime, lang, timezone, userData.user),
        convertToLocaleTimeString(row.startTime, lang, timezone, userData.user),
        row.delayDescription,
        convertToLocaleTimeString(row.endTime, lang, timezone, userData.user)
      ]
      data.push(temp)
    })
    const footValues = []

    const style = getStyle(getUserPartner(userData.user))

    addTable(doc, headers, data, footValues, style, 40)
    return doc
  }
}

const fileName = 'DelayedStartReport'
async function exportDelayedStartReportToExcel (userData, reportData) {
  console.log('Export to Excel')

  const translations = getTranslations(userData)
  const timezone = userData.user.attributes.timezone
  const lang = userData.user.attributes.lang

  const reportDataRows = reportData.devices

  const settings = {
    sheetName: translations.report.titleTripReport, // The name of the sheet
    fileName // The name of the spreadsheet
  }

  const headers = [
    { label: translations.report.group, value: 'group' },
    { label: translations.report.vehicle, value: 'name' },
    { label: translations.report.driver, value: 'driver' },
    { label: translations.report.day, value: 'day' },
    { label: translations.report.startDate, value: 'startDate' },
    { label: translations.report.startTime, value: 'startTime' },
    { label: translations.report.delayDescription, value: 'delayDescription' },
    { label: translations.report.endTime, value: 'endTime' }
  ]

  let data = []
  if (reportDataRows) {
    reportDataRows.forEach(row => {
      const group = userData.groups.find(g => row.groupId === g.id)
      data = data.concat({
        group: group ? group.name : '',
        name: row.device,
        driver: row.driver,
        day: row.weekDay,
        startDate: convertToLocaleDateString(row.startTime, lang, timezone, userData.user),
        startTime: convertToLocaleTimeString(row.startTime, lang, timezone, userData.user),
        delayDescription: row.delayDescription,
        endTime: convertToLocaleTimeString(row.endTime, lang, timezone, userData.user)
      })
    })
    console.log(data)
    return {
      headers,
      data,
      settings
    }
  }
}

exports.create = createDelayedStartReport
exports.exportToPDF = exportDelayedStartReportToPDF
exports.exportToExcel = exportDelayedStartReportToExcel
